/**
 * Bunker inside the Cispracs web app (voice#347). The platform frames Bunker
 * as its Chat section with `?platform=1`; this module decides whether this page
 * is that frame and speaks the small postMessage contract v1 with the host:
 *
 *   Bunker → host  cispracs:embedded-ready        { v: 1 }
 *   Bunker → host  cispracs:sign-in-required      { v: 1 }
 *   Bunker → host  cispracs:open-platform-view    { v: 1, view }
 *   host → Bunker  cispracs:platform-language     { v: 1, language: 'ru' | 'en' }
 *   host → Bunker  cispracs:deep-link             { v: 1, path }
 *   host → Bunker  cispracs:platform-account      { v: 1, platformId }   (lib/frameAccount.ts)
 *
 * Notifications live in the shell (owner's decision 21.10.2026, lib/shellNotifications.ts):
 *
 *   Bunker → host  cispracs:bunker-unread         { v: 1, unread, mentions, platformId }
 *   Bunker → host  cispracs:bunker-notification   { v: 1, id, kind, title, context, body,
 *                                                   avatarUrl, channelId, guildId, path, platformId }
 *   host → Bunker  cispracs:bunker-visibility     { v: 1, visible }
 *
 * The host is one of EMBED_HOSTS, named by `?embedOrigin=` or, failing that,
 * by document.referrer. Bunker posts only to that exact origin and accepts
 * messages only from it and only from the parent window. Unknown types are
 * ignored, so the contract can grow without breaking older builds.
 */

import { PLATFORM_HOSTS } from '@bunker/shared';
import { readPlatformAccount, setFrameAccount, startFrameAccountWait } from './frameAccount';
import { selectablePresence, type PresenceChannel } from './shellPresence';

const PROD_HOSTS: readonly string[] = PLATFORM_HOSTS;
// Andrey's local design preview frames Bunker the same way.
const DEV_HOSTS = ['http://localhost:3100', 'http://127.0.0.1:3100', 'https://localhost:3100', 'http://localhost:3115', 'http://127.0.0.1:3115'];

export function embedHosts(dev: boolean = import.meta.env.DEV): string[] {
  return dev ? [...PROD_HOSTS, ...DEV_HOSTS] : [...PROD_HOSTS];
}

/** The query flag that asks for embedded mode. */
export const EMBED_FLAG = 'platform';

const HOST_KEY = 'bunker.embedHost';
const AUTH_CHANNEL = 'bunker-auth';
const SIGNED_IN = 'bunker:signed-in';

/** Where the sign-in popup comes back to (a same-origin path the server accepts as returnTo). */
export const POPUP_DONE_PATH = '/auth/popup-done';

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

/**
 * detectEmbedHost returns the platform origin framing this page, or null when
 * the page is not the platform's Chat frame. It needs all of: being inside a
 * frame, `?platform=1`, and a host on the allowlist.
 */
export function detectEmbedHost(input: {
  search: string;
  referrer: string;
  inFrame: boolean;
  hosts: string[];
  remembered?: string | null;
  /** location.ancestorOrigins[0] where the browser has it: the real parent. */
  parentOrigin?: string | null;
}): string | null {
  if (!input.inFrame) return null;
  const q = new URLSearchParams(input.search);
  let host: string | null;
  if (q.get(EMBED_FLAG) === '1') {
    host = q.get('embedOrigin') ?? originOf(input.referrer);
  } else {
    // In-app navigation drops the query; the frame remembers its host.
    host = input.remembered ?? null;
  }
  if (!host || !input.hosts.includes(host)) return null;
  // Where the browser names the real parent, it must be that host.
  if (input.parentOrigin && input.parentOrigin !== host) return null;
  return host;
}

/** A deep link the host may ask for: a path on this site, never another origin. */
export function isSafeDeepLink(path: unknown): path is string {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.length > 512) return false;
  for (const ch of path) {
    const code = ch.charCodeAt(0);
    if (code < 0x20 || code === 0x7f || ch === '\\') return false;
  }
  return true;
}

let host: string | null = null;
let readyAnnounced = false;

/** The platform origin framing this page, or null in the normal app. */
export function embedHost(): string | null {
  return host;
}

export function isPlatformEmbedded(): boolean {
  return host !== null;
}

function readRemembered(): string | null {
  try {
    return sessionStorage.getItem(HOST_KEY);
  } catch {
    return null;
  }
}

function remember(value: string): void {
  try {
    sessionStorage.setItem(HOST_KEY, value);
  } catch {
    /* storage blocked: the query flag still works for this load */
  }
}

function post(type: string, payload: Record<string, unknown> = {}): void {
  if (!host) return;
  window.parent.postMessage({ v: 1, type, ...payload }, host);
}

export interface EmbedDeps {
  changeLanguage: (language: 'ru' | 'en') => void;
}

/**
 * installPlatformEmbed runs once at boot, before React renders. Outside the
 * platform frame it does nothing and returns false.
 */
export function installPlatformEmbed(deps: EmbedDeps): boolean {
  const inFrame = window.parent !== window;
  host = detectEmbedHost({
    search: window.location.search,
    referrer: document.referrer,
    inFrame,
    hosts: embedHosts(),
    remembered: inFrame ? readRemembered() : null,
    parentOrigin: window.location.ancestorOrigins?.[0] ?? null,
  });
  if (!host) return false;
  remember(host);
  document.documentElement.dataset.platformEmbedded = 'true';

  const lang = new URLSearchParams(window.location.search).get('lang');
  if (lang === 'ru' || lang === 'en') deps.changeLanguage(lang);

  // In-frame steps must not fill the host page's Back button: every in-app
  // navigation replaces the frame's history entry instead of adding one.
  const replace = window.history.replaceState.bind(window.history);
  window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => replace(data, unused, url);

  // Whose Bunker the host frames: until it says (or WAIT_MS passes) no chats are shown.
  startFrameAccountWait();

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== host || event.source !== window.parent) return;
    const data = event.data as { v?: unknown; type?: unknown; language?: unknown; path?: unknown; platformId?: unknown } | null;
    if (!data || data.v !== 1) return;
    if (data.type === 'cispracs:platform-account') {
      const platformId = readPlatformAccount(data);
      if (platformId) setFrameAccount(platformId);
    } else if (data.type === 'cispracs:platform-language' && (data.language === 'ru' || data.language === 'en')) {
      deps.changeLanguage(data.language);
    } else if (data.type === 'cispracs:deep-link' && isSafeDeepLink(data.path)) {
      replace(window.history.state, '', data.path);
      window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
    }
  });

  // A sign-in this frame started has finished in the popup: reload so the
  // app picks up the session. Only the nonce of our own pending sign-in
  // counts, so another page opening the return path cannot reload the frame
  // (and drop a call).
  const onSignedIn = (data: unknown) => {
    const msg = data as { type?: unknown; n?: unknown } | null;
    if (pendingNonce && msg?.type === SIGNED_IN && msg.n === pendingNonce) window.location.reload();
  };
  try {
    new BroadcastChannel(AUTH_CHANNEL).onmessage = (e) => onSignedIn(e.data);
  } catch {
    /* no BroadcastChannel: the opener message below still arrives */
  }
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin === window.location.origin) onSignedIn(event.data);
  });
  return true;
}

let pendingNonce: string | null = null;

/**
 * The status channel to the platform frame's host (lib/shellPresence.ts): Bunker posts
 * `{ v: 1, type: 'cispracs:bunker-presence', status, error?, platformId }`; the host asks with
 * `{ v: 1, type: 'cispracs:set-bunker-presence', status }`. Only the host that framed
 * Bunker, only the four selectable statuses. Null outside the frame.
 */
export function framePresenceChannel(platformId: () => string | null): PresenceChannel | null {
  const frameHost = host;
  if (!frameHost) return null;
  return {
    // Whose status it is: the host shows it only for its own user (cispracs/web#16).
    report: (status, error) => post('cispracs:bunker-presence', { status, ...(error ? { error } : {}), platformId: platformId() }),
    onSet: (cb) => onHostMessage(frameHost, 'cispracs:set-bunker-presence', (data) => {
      const status = selectablePresence(data.status);
      if (status) cb(status);
    }),
    // The host shows the status: `{ v: 1, type: 'cispracs:bunker-presence-hosted' }`.
    onHosted: (cb) => onHostMessage(frameHost, 'cispracs:bunker-presence-hosted', () => cb()),
  };
}

function onHostMessage(frameHost: string, type: string, cb: (data: Record<string, unknown>) => void): () => void {
  const listener = (event: MessageEvent) => {
    if (event.origin !== frameHost || event.source !== window.parent) return;
    const data = event.data as Record<string, unknown> | null;
    if (!data || data.v !== 1 || data.type !== type) return;
    cb(data);
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

/**
 * Tell the host this frame has no Bunker session and shows its sign-in. A
 * host whose visitor is signed in on the platform may send the whole page
 * (top level, never the frame) to Bunker's
 * `/api/auth/oidc/login?returnTo=<host page>`: Bunker signs in through the
 * platform without asking again and returns to that page, or to it with
 * `bunker_login=failed`. The host does this once per tab session, then
 * leaves the button.
 */
export function announceSignInRequired(): void {
  post('cispracs:sign-in-required');
}

/** The React shell is mounted; the host can bind the account before workspace hydration. */
export function announceEmbeddedReady(): void {
  if (!host || readyAnnounced) return;
  readyAnnounced = true;
  post('cispracs:embedded-ready');
}

/**
 * Where a link to the platform should open. Inside the platform's Chat frame
 * a link to the shell's own origin replaces the shell page (`_top`, on the
 * user's click) instead of opening a second copy of the platform in a new
 * tab; anything else, and every link outside the frame, opens a new tab.
 */
export function platformLinkTarget(url: string, frameHost: string | null = host): '_top' | '_blank' {
  if (!frameHost) return '_blank';
  try {
    return new URL(url).origin === frameHost ? '_top' : '_blank';
  } catch {
    return '_blank';
  }
}

/** What Bunker asks the host to announce: a message it would pop up itself if it were on screen. */
export interface HostNotification {
  /** The message id — the host can drop a repeat. */
  id: string;
  kind: 'dm' | 'mention' | 'reply';
  /** Who wrote it. */
  title: string;
  /** The chat or `#channel · server`, null for a one-to-one DM. */
  context: string | null;
  /** The text preview for a DM; for a server channel only "you were mentioned" — the text stays in Bunker. */
  body: string;
  /** An https avatar, or null. */
  avatarUrl: string | null;
  channelId: string;
  guildId: string | null;
  /** Where to send Bunker on a click: a `cispracs:deep-link` path. */
  path: string;
}

/** How many unread messages and mentions Bunker has, for the shell's «Бункер» item. */
export function announceUnread(unread: number, mentions: number, platformId: string | null): void {
  post('cispracs:bunker-unread', { unread, mentions, platformId });
}

/** Hand the host a pop-up Bunker would show itself if it were on screen. */
export function announceNotification(notification: HostNotification, platformId: string | null): void {
  post('cispracs:bunker-notification', { ...notification, platformId });
}

/** The host says whether its Chat section shows this frame. Returns the unsubscribe; a no-op outside the frame. */
export function onHostVisibility(cb: (visible: boolean) => void): () => void {
  if (!host) return () => undefined;
  return onHostMessage(host, 'cispracs:bunker-visibility', (data) => {
    if (typeof data.visible === 'boolean') cb(data.visible);
  });
}

/** Ask the host to show one of its own views (the host allowlists them). */
export function openPlatformView(view: 'profile'): void {
  post('cispracs:open-platform-view', { view });
}

/**
 * startEmbeddedSignIn begins a Cispracs ID sign-in from the frame in a
 * top-level popup: the identity pages are never framed. buildLoginHref turns
 * the return path into the sign-in URL. The popup returns to POPUP_DONE_PATH
 * with this attempt's nonce. `opened` is false when the browser blocked the
 * popup; the caller then lets its plain link open `href` in a new tab.
 */
export function startEmbeddedSignIn(buildLoginHref: (returnPath: string) => string): { href: string; opened: boolean } {
  pendingNonce = crypto.randomUUID();
  const href = buildLoginHref(`${POPUP_DONE_PATH}?n=${pendingNonce}`);
  const popup = window.open(href, 'bunker-signin', 'popup,width=520,height=720');
  return { href, opened: popup !== null };
}

/**
 * finishPopupSignIn runs on POPUP_DONE_PATH in the popup. It tells the frame
 * the session is ready, by BroadcastChannel (the frame and the popup are the
 * same site) and by the opener when the browser kept it, then tries to close.
 * A popup that cannot close itself shows a "you can close this window" note.
 */
export function finishPopupSignIn(search: string = window.location.search): void {
  const msg = { type: SIGNED_IN, n: new URLSearchParams(search).get('n') };
  try {
    new BroadcastChannel(AUTH_CHANNEL).postMessage(msg);
  } catch {
    /* fall through to the opener message */
  }
  const opener = window.opener as Window | null;
  if (opener) {
    try {
      opener.postMessage(msg, window.location.origin);
    } catch {
      /* the opener navigated away; the broadcast above still reached the frame */
    }
  }
  window.close();
}
